// ViewDress.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSwipeable } from "react-swipeable";
import icons from "../../icons.config";
import Navbar from '../Navbar/navbar';
import Footer from '../Footer/footer';
import FooterDoodles from '../Footer/footerDoodles';
import { useNavigate, useParams } from "react-router-dom";
import { MdStar, MdStarHalf, MdStarOutline } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import {
  addToFavourites,
  removeFromFavourites,
  fetchLikedItems,
} from "../../Providers/favouritesProvider";
import "./scroller.css";
import CustomSizeModal from "./modals/customSize";
import { selectClickedItem, setClickedItem } from "../../store/itemsSlice";
import { addToCart, fetchCartItems } from "../../Providers/cartProvider";
import CartSuccessPopup from "../Cart/cartSuccessPopup";
import SizeGuidePopup from "./modals/sizeGuidePopup";
import SelectSizePopup from "./modals/selectSizePopup";
import {
  getProductById,
  getAllRelatedProductsById,
} from "../../Providers/shopProvider";
import { fetchFAQs } from "../../Providers/contactUsProvider";
import { IMAGE_URL } from "../../api-urls";
import { setCustomSizeId } from "../../Providers/customSizeProvider";
import LoginPromptPopup from "./loginPromptPopup";
import ImageGallery from "./imageGallery";
import ProductDetails from "./productDetails";
import ReviewsSection from "./reviewsSection";
import RelatedProducts from "./relatedProducts";
import FAQsSection from "./faqsSection";
import ImageModal from "./modals/imageModal";

const ViewDress = ({ item, showTrigger }) => {
  const { category, productName, variant, variantId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated || false);

  // State management
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bigImage, setBigImage] = useState("");
  const [smallImages, setSmallImages] = useState([]);
  const [variantImages, setVariantImages] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [showCartSuccessPopup, setShowCartSuccessPopup] = useState(false);
  const [showSelectSizePopup, setShowSelectSizePopup] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginPromptMessage, setLoginPromptMessage] = useState(""); // New state for dynamic message
  const [selectedSmallImageIndex, setSelectedSmallImageIndex] = useState(0);
  const imagesPerView = 4;
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [itemData, setItemData] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [activeIndex, setActiveIndex] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleItems, setVisibleItems] = useState(3);
  const [autoPlay, setAutoPlay] = useState(true);
  const [isFading, setIsFading] = useState(false);
  const [imgWidth, setImgWidth] = useState(0);
  const [imgHeight, setImgHeight] = useState(0);
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [[x, y], setXY] = useState([0, 0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentBigIndex, setCurrentBigIndex] = useState(0);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [buttonText, setButtonText] = useState("ADD TO CART");
  const [selectedColor, setSelectedColor] = useState(null);

  const clickedItem = useSelector(selectClickedItem);
  const likedItems = useSelector((state) => state?.favourites?.likedItems || []);
  const userCustomSizeId = useSelector((state) => state.customSize.customSizeId);

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await getProductById(variantId);
        setProduct(response);
        const variantThumbnails = response.data?.selectedVariant.variant_thumbnail_images || [];
        const variantImages = response.data?.selectedVariant.variant_images || [];
        setSmallImages(variantThumbnails);
        setVariantImages(variantImages);
        setBigImage(variantImages[0] || '');
        dispatch(setClickedItem(response));
      } catch (error) {
        console.error("Error fetching product:", error);
        setError("Failed to fetch product details.");
      } finally {
        setLoading(false);
      }
    };
    if (variantId) fetchProduct();
  }, [variantId, dispatch]);

  // Fetch related products
  useEffect(() => {
    const fetchRelatedProducts = async () => {
      try {
        setLoading(true);
        const products = await getAllRelatedProductsById(variantId);
        setItemData([...products]);
      } catch (err) {
        setError("Failed to fetch related products");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (variantId) fetchRelatedProducts();
  }, [variantId]);

  // Fetch FAQs
  useEffect(() => {
    const getFAQs = async () => {
      const faqData = await fetchFAQs();
      setFaqs(faqData.data);
    };
    getFAQs();
  }, []);

  // Update images when clickedItem changes
  useEffect(() => {
    if (clickedItem && clickedItem.data?.selectedVariant) {
      setSmallImages(clickedItem.data.selectedVariant.variant_thumbnail_images || []);
      setVariantImages(clickedItem.data.selectedVariant.variant_images || []);
    }
  }, [clickedItem]);

  // Update reviews
  useEffect(() => {
    if (product?.data?.reviews) {
      const formattedReviews = product.data.reviews.map((review) => ({
        name: review.user_name,
        city: review.location,
        rating: Number(review.rating),
        review: review.comment,
      }));
      setReviews(formattedReviews);
    }
  }, [product]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setVisibleItems(2);
        setAutoPlay(true);
      } else {
        setVisibleItems(4);
        setAutoPlay(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Event handlers
  const handleSmallImageClick = (index) => {
    setBigImage(variantImages[index]);
    setSelectedSmallImageIndex(index);
    if (!isMobile && index === Math.min(imagesPerView - 1, smallImages.length - 1)) {
      setIsModalOpen(true);
    }
  };

  const getVisibleCarouselImages = () => smallImages;

  const handleCarouselNext = () => {
    const newIndex = (selectedSmallImageIndex + 1) % smallImages.length;
    setCurrentCarouselIndex(newIndex);
    setBigImage(variantImages[newIndex]);
    setSelectedSmallImageIndex(newIndex);
  };

  const handleCarouselPrev = () => {
    const newIndex = (selectedSmallImageIndex - 1 + smallImages.length) % smallImages.length;
    setCurrentCarouselIndex(newIndex);
    setBigImage(variantImages[newIndex]);
    setSelectedSmallImageIndex(newIndex);
  };

  const handlePreviousReview = () => {
    setCurrentReviewIndex((prevIndex) => (prevIndex === 0 ? reviews.length - 1 : prevIndex - 1));
  };

  const handleNextReview = () => {
    setCurrentReviewIndex((prevIndex) => (prevIndex === reviews.length - 1 ? 0 : prevIndex + 1));
  };

  const toggleFAQ = (index) => setActiveIndex(activeIndex === index ? null : index);

  const handleNext = () => {
    setIsFading(true);
    setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % itemData.length);
      setIsFading(false);
    }, 500);
  };

  const handlePrev = () => {
    setIsFading(true);
    setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex - 1 + itemData.length) % itemData.length);
      setIsFading(false);
    }, 500);
  };

  const getVisibleDresses = () => {
    const endIndex = currentIndex + visibleItems;
    return itemData.slice(currentIndex, endIndex);
  };

  const handleDotClick = (index) => {
    setIsFading(true);
    setTimeout(() => {
      setCurrentIndex(index * visibleItems);
      setIsFading(false);
    }, 500);
  };

  const handlers = useSwipeable({ onSwipedLeft: handleNext, onSwipedRight: handlePrev, preventDefaultTouchmoveEvent: true, trackMouse: true });
  const handlers1 = useSwipeable({ onSwipedLeft: handleNextReview, onSwipedRight: handlePreviousReview, preventDefaultTouchmoveEvent: true, trackMouse: true });
  const carouselHandlers = useSwipeable({ onSwipedLeft: handleCarouselNext, onSwipedRight: handleCarouselPrev, preventDefaultTouchmoveEvent: true, trackMouse: true });
  const bigImageHandlers = useSwipeable({ onSwipedLeft: handleCarouselNext, onSwipedRight: handleCarouselPrev, preventDefaultTouchmoveEvent: true, trackMouse: true });

  const renderStars = (rating) => {
    if (!rating) return null;
    const numericRating = Number(rating);
    const fullStars = Math.floor(numericRating);
    const halfStars = numericRating % 1 !== 0 ? 1 : 0;
    const emptyStars = 5 - fullStars - halfStars;
    return (
      <div className="flex space-x-1">
        {[...Array(fullStars)].map((_, index) => <MdStar key={`full-${index}`} className="text-review" />)}
        {halfStars === 1 && <MdStarHalf className="text-review" />}
        {[...Array(emptyStars)].map((_, index) => <MdStarOutline key={`empty-${index}`} className="text-review" />)}
      </div>
    );
  };

  const magnifierHeight = 650;
  const magnifierWidth = 470;
  const zoomLevel = 3;

  const handleNextBig = () => {
    if (variantImages.length === 0) return;
    setIsFading(true);
    setTimeout(() => {
      setCurrentBigIndex((prevIndex) => {
        const newIndex = (prevIndex + 1) % variantImages.length;
        setBigImage(variantImages[newIndex]);
        setSelectedSmallImageIndex(newIndex);
        return newIndex;
      });
      setIsFading(false);
    }, 500);
  };

  const handlePrevBig = () => {
    if (variantImages.length === 0) return;
    setIsFading(true);
    setTimeout(() => {
      setCurrentBigIndex((prevIndex) => {
        const newIndex = (prevIndex - 1 + variantImages.length) % variantImages.length;
        setBigImage(variantImages[newIndex]);
        setSelectedSmallImageIndex(newIndex);
        return newIndex;
      });
      setIsFading(false);
    }, 500);
  };

  const handleLike = async (variantId) => {
    if (!isAuthenticated) {
      setLoginPromptMessage("Please log in first to add items to your wishlist!");
      setShowLoginPrompt(true);
      return;
    }
    if (!variantId) return;
    const likedItem = likedItems.find((product) => String(product.variant_id) === String(variantId));
    if (likedItem) return;
    try {
      dispatch(addToFavourites(variantId));
      dispatch(fetchLikedItems());
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  const toggleSizeGuide = () => {
    setIsSizeGuideOpen((prev) => {
      if (!prev) document.body.classList.add("no-scroll");
      else document.body.classList.remove("no-scroll");
      return !prev;
    });
  };

  const handleSizeSelection = (size) => setSelectedSize(size);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      setLoginPromptMessage("Please log in first to add items to your cart!");
      setShowLoginPrompt(true);
      return;
    }
    if (!clickedItem?.data?.selectedVariant?.id) return;
    if (!selectedSize && !userCustomSizeId) {
      setShowSelectSizePopup(true);
      return;
    }
    const quantityToAdd = quantity || 1;
    const variantId = clickedItem?.data?.selectedVariant?.id;
    const size = selectedSize || null;
    const color = clickedItem?.data?.base_color;
    const sizeData = size ? { size } : userCustomSizeId ? { customSizeId: userCustomSizeId } : {};
    try {
      await dispatch(addToCart({ variantId, quantity: quantityToAdd, color, ...sizeData }));
      setShowCartSuccessPopup(true);
      await fetchCartItems();
    } catch (err) {
      console.error("Add to cart failed:", err);
    }
    setButtonText("ADDED TO CART!");
    setTimeout(() => setButtonText("ADD TO CART"), 3000);
  };

  const handleColorSelection = (clickedColor) => {
    setSelectedColor(clickedColor.color_code);
    navigate(`/${clickedColor.url}`);
  };

  const handleHeartClick = async (item) => {
    if (!isAuthenticated) {
      setLoginPromptMessage("Please log in first to add items to your wishlist!");
      setShowLoginPrompt(true);
      return;
    }
    if (!item?.default_variant) {
      console.error("❌ Error: item or default_variant is undefined", item);
      return;
    }
    const likedItem = likedItems.find((likedItem) => String(likedItem.variant_id) === String(item.default_variant.id));
    try {
      if (likedItem) {
        dispatch(removeFromFavourites(item.default_variant.id));
        await new Promise((resolve) => setTimeout(resolve, 500));
        dispatch(fetchLikedItems());
      } else {
        dispatch(addToFavourites(item.default_variant.id));
        dispatch(fetchLikedItems());
      }
    } catch (error) {
      console.error("❌ API Error:", error);
    }
  };

  const handleItemClick = (item) => {
    dispatch(setClickedItem(item));
    const formatText = (text) => encodeURIComponent(text.replace(/\s+/g, ""));
    const category = formatText(item.product_category[0].toLowerCase());
    const productName = formatText(item.product_name);
    const variant = formatText(item.default_variant.title);
    const variantId = item.default_variant.id;
    navigate(`/${category}/${productName}/${variant}/${variantId}/buy`);
  };

  const remainingImages = smallImages.length > imagesPerView ? smallImages.length - imagesPerView : 0;
  const sku = product?.data?.sku || "";
  const productId = product?.data?._id || "";

  return (
    <div className="wrapper w-full h-full flex flex-col">
      <Navbar />
      <div className="w-full min-h-screen bg-tertiary scroll-container">
        <div className="content-wrapper">
          <div className="relative px-7 lg:px-0 z-10 main-content" {...handlers1}>
            <div className="hidden lg:flex absolute right-0">
              <img src={icons.viewPageCircles} alt="circles icon" className="w-48 h-48 lg:h-64 lg:w-64" />
            </div>
            {isCustomModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
                <CustomSizeModal onClose={() => setIsCustomModalOpen(false)} />
              </div>
            )}
            <div className="max-w-full lg:max-w-[82%] xl:max-w-[83%] 2xl:max-w-[84%] mx-auto px-1 pt-28 md:pt-32 pb-10 lg:pb-0 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="flex flex-col">
                <ImageGallery
                  isMobile={isMobile}
                  bigImage={bigImage}
                  smallImages={smallImages}
                  variantImages={variantImages}
                  selectedSmallImageIndex={selectedSmallImageIndex}
                  imagesPerView={imagesPerView}
                  remainingImages={remainingImages}
                  handleSmallImageClick={handleSmallImageClick}
                  getVisibleCarouselImages={getVisibleCarouselImages}
                  bigImageHandlers={bigImageHandlers}
                  carouselHandlers={carouselHandlers}
                  showMagnifier={showMagnifier}
                  setShowMagnifier={setShowMagnifier}
                  setImgWidth={setImgWidth}
                  setImgHeight={setImgHeight}
                  setXY={setXY}
                  magnifierWidth={magnifierWidth}
                  magnifierHeight={magnifierHeight}
                  zoomLevel={zoomLevel}
                  x={x}
                  y={y}
                  imgWidth={imgWidth}
                  imgHeight={imgHeight}
                />
                {!isMobile && (
                  <ReviewsSection
                    isMobile={isMobile}
                    reviews={reviews}
                    currentReviewIndex={currentReviewIndex}
                    handlePreviousReview={handlePreviousReview}
                    handleNextReview={handleNextReview}
                    renderStars={renderStars}
                    setCurrentReviewIndex={setCurrentReviewIndex}
                    productId={productId}
                    sku={sku}
                    isAuthenticated={isAuthenticated}
                    setShowLoginPrompt={() => {
                      setLoginPromptMessage("Please log in first to add a review!");
                      setShowLoginPrompt(true);
                    }}
                  />
                )}
              </div>
              <div className="flex flex-col">
                <ProductDetails
                  product={product}
                  quantity={quantity}
                  setQuantity={setQuantity}
                  selectedSize={selectedSize}
                  handleSizeSelection={handleSizeSelection}
                  isSizeGuideOpen={isSizeGuideOpen}
                  toggleSizeGuide={toggleSizeGuide}
                  variantId={variantId}
                  selectedColor={selectedColor}
                  handleColorSelection={handleColorSelection}
                  buttonText={buttonText}
                  handleAddToCart={handleAddToCart}
                  handleLike={handleLike}
                  showCartSuccessPopup={showCartSuccessPopup}
                  setShowCartSuccessPopup={setShowCartSuccessPopup}
                  setIsCustomModalOpen={setIsCustomModalOpen}
                  faqs={faqs}
                  activeIndex={activeIndex}
                  toggleFAQ={toggleFAQ}
                  isAuthenticated={isAuthenticated}
                />
                {isMobile && (
                  <ReviewsSection
                    isMobile={isMobile}
                    reviews={reviews}
                    currentReviewIndex={currentReviewIndex}
                    handlePreviousReview={handlePreviousReview}
                    handleNextReview={handleNextReview}
                    renderStars={renderStars}
                    setCurrentReviewIndex={setCurrentReviewIndex}
                    productId={productId}
                    sku={sku}
                    isAuthenticated={isAuthenticated}
                    setShowLoginPrompt={() => {
                      setLoginPromptMessage("Please log in first to add a review!");
                      setShowLoginPrompt(true);
                    }}
                  />
                )}
              </div>
            </div>
            <RelatedProducts
              itemData={itemData}
              getVisibleDresses={getVisibleDresses}
              isFading={isFading}
              handleNext={handleNext}
              handlePrev={handlePrev}
              handleDotClick={handleDotClick}
              visibleItems={visibleItems}
              currentIndex={currentIndex}
              dispatch={dispatch}
              navigate={navigate}
              likedItems={likedItems}
              handleHeartClick={handleHeartClick}
              handlers={handlers}
              handleItemClick={handleItemClick}
            />
            {showSelectSizePopup && (
              <SelectSizePopup message="Please select a size first!" onClose={() => setShowSelectSizePopup(false)} />
            )}
            {showLoginPrompt && (
              <LoginPromptPopup
                message={loginPromptMessage}
                onClose={() => {
                  setShowLoginPrompt(false);
                  setLoginPromptMessage(""); // Reset message when closing
                }}
              />
            )}
          </div>
          <FooterDoodles />
          <Footer />
          {!isMobile && isModalOpen && (
            <ImageModal
              isOpen={isModalOpen}
              handleCloseModal={() => setIsModalOpen(false)}
              bigImage={IMAGE_URL + bigImage}
              smallImages={smallImages.map(img => IMAGE_URL + img)}
              clickedItem={clickedItem}
              handleImageClick={(image, index) => {
                setBigImage(variantImages[index]);
                setSelectedSmallImageIndex(index);
              }}
              handleNextBig={handleNextBig}
              handlePrevBig={handlePrevBig}
              icons={icons}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewDress;